import {
  MetaApprovalStatusDefinition,
  SendLimitDefinition,
  WhatsAppSendLimit,
  WhatsAppTemplateCategoryDefinition,
  WhatsAppTemplateRejectionReason,
} from 'modules/graphql';
import { WHATSAPP_SEND_LIMIT_DISPLAY_MAPPING } from 'modules/sendingPlatforms/constants/text';

// the default iterable tracking domain that will be used for link shortening,
// if our users do not set their own custom domain

export const DEFAULT_LINK_SHORTENING_TRACKING_DOMAIN = process.env.LINK_SHORTENING_TRACKING_DOMAIN;

export const DOC_INFO_BANNER_DISPLAY_STORAGE_KEY = 'whats-app-template-editor-info-banner-seen';

export const META_TEMPLATE_DOCUMENTATION_LINK =
  'https://developers.facebook.com/docs/whatsapp/message-templates/guidelines/';

export const META_TEMPLATE_DOCUMENTATION_LINK_REJECTION_INFO =
  'https://developers.facebook.com/docs/whatsapp/message-templates/guidelines/#common-rejection-reasons';

export const WHATS_APP_BUSINESS_POLICY_LINK = `https://business.whatsapp.com/policy`;

export const WHATS_APP_NEW_TEMPLATE_GUIDELINES_LINK =
  'https://developers.facebook.com/docs/whatsapp/updates-to-pricing/new-template-guidelines';

export const WHATS_APP_CREATING_TEMPLATE_SUPPORT_DOCS_LINK =
  'https://support.iterable.com/hc/articles/36861410753812';

export const SETTINGS_DISPLAY_CONTENT = {
  unsetText: 'None',
  header: 'Header',
  imageHeader: 'Image',
  customTextFieldsDefaultLabel: 'New field',
  buttons: {
    labelText: 'Buttons',
    buttonText: 'Add buttons',
    defaultButtonTitle: 'New button',
    labels: {
      url: 'Website URL',
      buttonText: 'Button text',
      phoneNumber: 'Phone number',
    },
    helpText: {
      buttonText: 'Supports text only',
      url: 'URL should start with https:// or http:// and can have up to one link parameter',
    },

    ids: {
      radioOpenAppId: 'radio-action-open-app',
      radioTrackOnlyId: 'radio-action-track-only',
      radioOpenUrlId: 'radio-action-open-url',
      radioCustomActionId: 'radio-action-custom',
      inputFieldOpenUrlId: 'input-open-url',
      inputFieldCustomActionId: 'input-custom-action',
      inputFieldIdentifierId: 'identifier-input-text',
      inputFieldTitleId: 'submit-button-input-text',
      inputFieldPlaceholderId: 'placeholder-input-text',
      buttonTextFieldId: 'button-text',
      urlFieldId: 'url-text',
      inputTextTitleId: 'edit-action-button-title',
      phoneFieldId: 'phone-number',
    },
  },
  message: {
    disabled:
      'This project has disabled opt out instructions. This can negatively impact your deliverability.',
    labelText: 'Message',
  },
  footer: {
    labelText: 'Footer',
    placeholder: 'Enter a footer...',
  },
  sender: {
    labelText: 'Sender',
    placeholder: 'Select a sender...',
    restrictedStatusTooltipContent: (limit?: SendLimitDefinition) =>
      `Limit reached${limit && limit !== WhatsAppSendLimit.LimitNa ? ` (${WHATSAPP_SEND_LIMIT_DISPLAY_MAPPING[limit]?.friendlyNumber} new conversations in 24 hours)` : ``}. New messages will be skipped, but replies to ongoing conversations are allowed. New conversation ability will gradually restore.`,
    lowQualityTooltipContent:
      'Senders flagged for low quality. Limit upgrades blocked; may decrease in 7 days if not improved.',
  },
  category: {
    labelText: 'WhatsApp Category',
    placeholder: 'Select a category...',
    categoryHelp: 'Required by Meta to review your content.',
  },
  shortenAndTrackLinks: {
    checkboxLabel: 'Shorten links to enable click tracking and custom link parameters',
    exampleLink: `https://${DEFAULT_LINK_SHORTENING_TRACKING_DOMAIN}/-KT~2NMwZ`,
    helpText: 'Example of a shortened link:',
    labelText: 'Shorten and track',
    noDomainsErrorText:
      'This project will use Iterable’s default domain. Visit Settings > Domains to setup custom tracking domains.',
  },
  statusBannerContent: {
    [MetaApprovalStatusDefinition.FirstPaused]: {
      title:
        'This template is paused for the next 3 hours due to negative feedback or low engagement. Here’s what you can do:',
      suggestions: [
        {
          secondaryText:
            'Deactivate or cancel the schedule for any campaigns using this template for the next 3 hours',
        },
        // TODO: add back once link has been finalized - MOB-11223
        // {
        //   linkPrefix: {
        //     text: 'See tips',
        //     href: '#',
        //   },
        //   secondaryText: ' for driving high quality conversations',
        // },
      ],
    },
    [MetaApprovalStatusDefinition.SecondPaused]: {
      title:
        'This template is paused for the next 6 hours due to negative feedback or low engagement and is at risk of being disabled by Meta. Here’s what you can do:',
      suggestions: [
        {
          secondaryText:
            'Deactivate or cancel the schedule for any campaigns using this template for the next 6 hours',
        },
        // TODO: add back once link has been finalized - MOB-11223
        // {
        //   linkPrefix: {
        //     text: 'See tips',
        //     href: '#',
        //   },
        //   secondaryText: ' for driving high quality conversations',
        // },
      ],
    },
    [MetaApprovalStatusDefinition.Disabled]: {
      title:
        'The template has been disabled due to recurring negative feedback or low engagement. Campaigns using this template will not be delivered to customers.',
      suggestions: [
        {
          secondaryText: 'Deactivate any campaigns using this template',
        },
      ],
    },
    [WhatsAppTemplateRejectionReason.ABUSIVE_CONTENT]: {
      title:
        'This template was rejected by WhatsApp for containing abusive or threatening language. Here’s what you can do:',
      suggestions: [
        {
          primaryText: 'Avoid aggressive language: ',
          secondaryText:
            'Remove any language that could be interpreted as abusive, threatening, or intimidating.',
          linkSuffix: null,
        },
        {
          primaryText: 'Review policies and guidelines: ',
          secondaryText: 'Ensure your message complies with ',
          linkSuffix: {
            text: "WhatsApp's Business and Commerce Policies",
            href: WHATS_APP_BUSINESS_POLICY_LINK,
          },
        },
        {
          primaryText: 'Respectful tone: ',
          secondaryText:
            'Focus on maintaining a neutral, respectful, and professional tone throughout your message.',
          linkSuffix: null,
        },
      ],
    },
    [WhatsAppTemplateRejectionReason.SCAM]: {
      title:
        'This template was rejected by WhatsApp for being flagged as potentially scam-like. Here’s what you can do:',
      suggestions: [
        {
          primaryText: 'Clarify intent: ',
          secondaryText: 'Ensure the template clearly communicates a legitimate business purpose.',
          linkSuffix: null,
        },
        {
          primaryText: 'Avoid misleading Content: ',
          secondaryText: 'Remove any text that could be interpreted as deceptive or fraudulent.',
          linkSuffix: null,
        },
        {
          primaryText: 'Provide transparency: ',
          secondaryText:
            'Clearly state your business’s name, the value or offer, and how the recipient can benefit.',
          linkSuffix: null,
        },
      ],
    },
    [WhatsAppTemplateRejectionReason.INCORRECT_CATEGORY]: {
      title:
        'This template was rejected by WhatsApp because it was submitted under the wrong category. Here’s what you can do:',
      suggestions: [
        {
          primaryText: 'Select the right category: ',
          secondaryText:
            "Review WhatsApp's template categories and ensure your message fits the chosen category (e.g., transactional, promotional, etc.).",
          linkSuffix: null,
        },
        {
          primaryText: 'Resubmit: ',
          secondaryText: 'Correct the WhatsApp category selection and resubmit the template.',
          linkSuffix: null,
        },
      ],
    },
    [WhatsAppTemplateRejectionReason.INVALID_FORMAT]: {
      title:
        'This template was rejected by WhatsApp due to formatting issues. Here’s what you can do:',
      suggestions: [
        {
          primaryText: 'Avoid special characters: ',
          secondaryText: 'Remove any special characters like #, $, or % from merge parameters.',
          linkSuffix: null,
        },
        {
          primaryText: 'Ensure message completeness: ',
          secondaryText:
            'Avoid ending your message with a merge parameter, and aim for a higher text-to-dynamic content ratio, especially if the message is brief.',
          linkSuffix: null,
        },
        {
          primaryText: 'Preview your template: ',
          secondaryText:
            'Check that your message flows naturally with merge parameters and preview how it will look once replaced with actual data to identify any issues.',
          linkSuffix: null,
        },
      ],
    },
    [WhatsAppTemplateRejectionReason.UNKNOWN]: {
      title: 'This template was rejected by WhatsApp. Here’s what you can do:',
      suggestions: [
        {
          primaryText: 'Review policies and guidelines: ',
          secondaryText: 'Ensure your message complies with ',
          linkSuffix: {
            text: "WhatsApp's Business and Commerce Policies",
            href: WHATS_APP_BUSINESS_POLICY_LINK,
          },
        },
        {
          primaryText: 'Prevent duplicates: ',
          secondaryText: 'Check for duplicate templates with identical wording to avoid rejection.',
          linkSuffix: null,
        },
        {
          primaryText: 'Simplify your template: ',
          secondaryText:
            'Try simplifying the language and structure to remove any potential confusion.',
          linkSuffix: null,
        },
        {
          primaryText: 'Review formatting: ',
          secondaryText:
            'Double-check your template for any formatting errors or special characters.',
          linkSuffix: null,
        },
        {
          primaryText: 'Contact support: ',
          secondaryText: 'If the issue persists, consider reaching out to your CSM for support.',
          linkSuffix: null,
        },
      ],
    },
    [WhatsAppTemplateRejectionReason.NONE]: {
      title: 'This template was rejected by WhatsApp. Here’s what you can do:',
      suggestions: [
        {
          primaryText: 'Review policies and guidelines: ',
          secondaryText: 'Ensure your message complies with ',
          linkSuffix: {
            text: "WhatsApp's Business and Commerce Policies",
            href: WHATS_APP_BUSINESS_POLICY_LINK,
          },
        },
        {
          primaryText: 'Prevent duplicates: ',
          secondaryText: 'Check for duplicate templates with identical wording to avoid rejection.',
        },
        {
          primaryText: 'Simplify your template: ',
          secondaryText:
            'Try simplifying the language and structure to remove any potential confusion.',
        },
        {
          primaryText: 'Review formatting: ',
          secondaryText:
            'Double-check your template for any formatting errors or special characters.',
        },
        {
          primaryText: 'Contact support: ',
          secondaryText: 'If the issue persists, consider reaching out to your CSM for support.',
        },
      ],
    },
  },
};

export const MAX_FOOTER_LENGTH = 60;

export const NEW_MAX_MESSAGE_LENGTH_BUSINESS = 550;
export const MAX_MESSAGE_LENGTH_BUSINESS = 1024;
export const MAX_MESSAGE_LENGTH_USER = 4096;

export const MAX_HEADER_LENGTH = 60;

export const MAX_BUTTON_TITLE_LENGTH = 25;
export const MAX_BUTTON_URL_LENGTH = 2000;

export const WHATSAPP_CATEGORY_OPTIONS = [
  // {
  //   label: WhatsAppTemplateCategoryDefinition.Authentication,
  //   id: WhatsAppTemplateCategoryDefinition.Authentication,
  //   value: WhatsAppTemplateCategoryDefinition.Authentication,
  // },
  {
    label: WhatsAppTemplateCategoryDefinition.Marketing,
    id: WhatsAppTemplateCategoryDefinition.Marketing,
    value: WhatsAppTemplateCategoryDefinition.Marketing,
    doubleLine:
      'Engage customers with personalized messages. Not allowed for recipients in the U.S. per WhatsApp policy.',
  },
  // {
  //   label: WhatsAppTemplateCategoryDefinition.Service,
  //   id: WhatsAppTemplateCategoryDefinition.Service,
  //   value: WhatsAppTemplateCategoryDefinition.Service,
  // },
  {
    label: WhatsAppTemplateCategoryDefinition.Utility,
    id: WhatsAppTemplateCategoryDefinition.Utility,
    value: WhatsAppTemplateCategoryDefinition.Utility,
    doubleLine: 'Transactional updates like order confirmation',
  },
];

export const BI_IMAGE_HEADER_EDITABLE_STATUSES = [
  MetaApprovalStatusDefinition.Approved,
  MetaApprovalStatusDefinition.Reinstated,
  MetaApprovalStatusDefinition.FirstPaused,
  MetaApprovalStatusDefinition.SecondPaused,
];
